
import React, { useEffect, useRef, useState } from "react";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "down" | "left" | "right" | "scale" | "fade";
  distance?: string;
  duration?: number;
  easing?: string;
  threshold?: number;
  once?: boolean;
  staggerChildren?: boolean;
  staggerDelay?: number;
}

const ScrollReveal = ({ 
  children, 
  className = "", 
  delay = 0,
  direction = "up",
  distance = "30px",
  duration = 600,
  easing = "cubic-bezier(0.5, 0, 0, 1)",
  threshold = 0.1,
  once = true,
  staggerChildren = false,
  staggerDelay = 100
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
            
            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold,
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [delay, once, threshold]);

  // Set initial styles based on direction
  let initialStyles = {};
  let activeStyles = {};
  
  switch (direction) {
    case "up":
      initialStyles = { transform: `translateY(${distance})`, opacity: 0 };
      activeStyles = { transform: "translateY(0)", opacity: 1 };
      break;
    case "down":
      initialStyles = { transform: `translateY(-${distance})`, opacity: 0 };
      activeStyles = { transform: "translateY(0)", opacity: 1 };
      break;
    case "left":
      initialStyles = { transform: `translateX(${distance})`, opacity: 0 };
      activeStyles = { transform: "translateX(0)", opacity: 1 };
      break;
    case "right":
      initialStyles = { transform: `translateX(-${distance})`, opacity: 0 };
      activeStyles = { transform: "translateX(0)", opacity: 1 };
      break;
    case "scale":
      initialStyles = { transform: "scale(0.9)", opacity: 0 };
      activeStyles = { transform: "scale(1)", opacity: 1 };
      break;
    case "fade":
      initialStyles = { opacity: 0 };
      activeStyles = { opacity: 1 };
      break;
    default:
      initialStyles = { transform: `translateY(${distance})`, opacity: 0 };
      activeStyles = { transform: "translateY(0)", opacity: 1 };
  }

  // Create transition style
  const transitionStyle = {
    transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
    ...(isVisible ? activeStyles : initialStyles)
  };

  // Handle staggered children if needed
  const childrenWithStagger = !staggerChildren ? children : 
    React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) return child;
      
      // Fix: Need to properly merge styles instead of overwriting
      return React.cloneElement(child, {
        ...child.props,
        style: {
          ...(child.props.style || {}),
          transitionDelay: `${delay + (index * staggerDelay)}ms`,
        },
      });
    });

  return (
    <div 
      ref={ref} 
      className={`reveal ${className}`}
      style={transitionStyle}
    >
      {childrenWithStagger}
    </div>
  );
};

export default ScrollReveal;
